import { useLocation } from "react-router-dom";
import { useQuote } from "./useQuote";
import { ROUTES } from "routes/constants";
import { useProductConfig, useProductContext } from "context/ProductContext";
import { getMLTADescription } from "utils/mltaQuoteUtils";

interface StickyBannerData {
    shouldShow: boolean;
    pageId: "invitation" | "quote-results" | null;
    quote?: string;
    coverageAmount?: string;
    mltaDescription?: string;
    isInvitationPage: boolean;
    isQuotePage: boolean;
    isQuoteResultsPage: boolean;
    isMLTAQuotePage: boolean;
}

export const useStickyBannerData = (): StickyBannerData => {
    const { pathname } = useLocation();
    const { quoteData } = useQuote();
    const { isMLTA } = useProductContext();
    const config = useProductConfig();

    // Check if we should show the StickyBanner (on invitation page or quote page)
    const isInvitationPage = pathname === ROUTES.INVITATION;
    const isQuotePage = pathname === ROUTES.QUOTE;
    const isQuoteResultsPage = pathname === ROUTES.QUOTE_RESULTS;
    const isMLTAQuotePage = isMLTA && isQuotePage;
    const shouldShow = isInvitationPage || isQuotePage || isQuoteResultsPage;

    if (!shouldShow) {
        return {
            shouldShow: false,
            pageId: null,
            isInvitationPage,
            isQuotePage,
            isQuoteResultsPage,
            isMLTAQuotePage,
        };
    }

    const pageId = isInvitationPage ? "invitation" : "quote-results";

    // Get sticky banner data only for quote page
    let quote: string | undefined;
    let coverageAmount: string | undefined;

    if (isQuotePage || isQuoteResultsPage) {
        if (isMLTA) {
            // MLTA: Use selectedCoveragePremium from Redux (persisted by MLTAQuoteResultsPage)
            if (quoteData?.selectedCoveragePremium) {
                quote = quoteData.selectedCoveragePremium.toFixed(2);
            } else if (quoteData?.mltaBenefits && quoteData.mltaBenefits.length > 0) {
                // Fallback to first benefit's individual price
                quote = quoteData.mltaBenefits[0].individual.toFixed(2);
            } else {
                quote = "-";
            }
            // MLTA doesn't use coverageAmount - it uses mltaDescription instead
        } else {
            // DTO: Use selectedCoverageAmount and selectedCoveragePremium
            if (quoteData?.selectedCoverageAmount && quoteData?.selectedCoveragePremium) {
                quote = quoteData.selectedCoveragePremium.toString();
                coverageAmount = quoteData.selectedCoverageAmount.toLocaleString();
            } else if (quoteData?.coverageOptions && quoteData.coverageOptions.length > 0) {
                // User has coverage options but hasn't selected yet - show first option
                const firstOption = quoteData.coverageOptions[0];
                quote = firstOption.monthlyPremium.toString();
                coverageAmount = firstOption.coverageAmount.toLocaleString();
            } else {
                // No coverage options yet - show default values
                quote = "-";
                coverageAmount = "100,000";
            }
        }
    }

    // MLTA description (e.g., "Superior Family Coverage") - use product name as fallback for sticky banner
    const mltaDescription = getMLTADescription(quoteData?.selectedTier, quoteData?.selectedCoverageType, config.displayName);

    return {
        shouldShow,
        pageId,
        quote,
        coverageAmount,
        mltaDescription,
        isInvitationPage,
        isQuotePage,
        isQuoteResultsPage,
        isMLTAQuotePage,
    };
};
